// qs-type-modes.ts
export type ModeType = 
  | 'drawing'
  | 'delete'
  | 'pan'
  | 'position'
  | 'text'
  | null

export type ModeCategory = 
  | 'drawing'
  | 'editing'
  | 'navigation'

export interface ModeConfig {
  id: ModeType
  name: string
  description: string
  category: ModeCategory
  icon?: string
  keyboardShortcut?: string
  cursor?: string
  isDestructive?: boolean
  requiresConfirmation?: boolean
}

export interface ModeState {
  activeMode: ModeType
  previousMode: ModeType | null
  isTransitioning: boolean
  modeStartTime: number | null
  activeLayerId?: string | null
}

export interface ModeEvent {
  type: 'mode-activated' | 'mode-deactivated' | 'mode-switched' | 'mode-failed'
  mode: ModeType
  previousMode: ModeType | null
  timestamp: number
  reason?: string
}

export interface ModeValidation {
  isValid: boolean
  canActivate: boolean
  reason?: string
  requiredActions?: string[]
}

export const MODE_REGISTRY: Record<NonNullable<ModeType>, ModeConfig> = {
  'drawing': {
    id: 'drawing',
    name: 'Draw',
    description: 'Standard brush drawing mode',
    category: 'drawing',
    icon: '✏️',
    keyboardShortcut: 'Escape',
    cursor: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'20\' height=\'20\' viewBox=\'0 0 20 20\'%3E%3Ccircle cx=\'10\' cy=\'10\' r=\'2\' fill=\'%23000\' stroke=\'%23fff\' stroke-width=\'1\'/%3E%3C/svg%3E") 10 10, crosshair'
  },
  'delete': {
    id: 'delete',
    name: 'Delete',
    description: 'Delete content from canvas',
    category: 'editing',
    icon: '🗑️',
    keyboardShortcut: 'D',
    cursor: 'crosshair',
    isDestructive: true,
    requiresConfirmation: true
  },
  'pan': {
    id: 'pan',
    name: 'Pan',
    description: 'Move the canvas/artboard to navigate your drawing',
    category: 'navigation',
    icon: '✋',
    keyboardShortcut: 'M',
    cursor: 'grab'
  },
  'position': {
    id: 'position',
    name: 'Position',
    description: 'Move layer position on canvas',
    category: 'editing',
    icon: '🔄',
    keyboardShortcut: undefined,
    cursor: 'move'
  },
  'text': {
    id: 'text',
    name: 'Text',
    description: 'Place text on canvas',
    category: 'drawing',
    icon: 'T',
    keyboardShortcut: 'T',
    cursor: 'text'
  }
}

export const getModeConfig = (mode: ModeType): ModeConfig | null => {
  if (!mode) return null
  return MODE_REGISTRY[mode] || null
}

export const getModesByCategory = (category: ModeCategory): ModeConfig[] => {
  return Object.values(MODE_REGISTRY).filter(mode => mode.category === category)
}

export const isDrawingMode = (mode: ModeType): boolean => {
  return mode === 'drawing'
}

export const isNavigationMode = (mode: ModeType): boolean => {
  return mode === 'pan'
}

export const isEditingMode = (mode: ModeType): boolean => {
  return mode === 'delete' || mode === 'position'
}

export const isDestructiveMode = (mode: ModeType): boolean => {
  const config = getModeConfig(mode)
  return config?.isDestructive || false
}

export const requiresConfirmation = (mode: ModeType): boolean => {
  const config = getModeConfig(mode)
  return config?.requiresConfirmation || false
}